import { useState, useEffect } from 'react';
import { bankingAPI } from '../../api';
import SlideOver from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';

/**
 * TransferBetweenAccountsSlideOver - Log a transfer between two bank accounts
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - onSuccess: () => void - Called after a successful transfer (e.g. refresh lists)
 */
const TransferBetweenAccountsSlideOver = ({ open, onOpenChange, onSuccess }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    from_account_id: '',
    to_account_id: '',
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      setError('');
      setFormData({
        from_account_id: '',
        to_account_id: '',
        amount: '',
        transaction_date: new Date().toISOString().split('T')[0],
        reference: '',
        notes: '',
      });
      fetchAccounts();
    }
  }, [open]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await bankingAPI.getAccounts({ include_inactive: false });
      setAccounts(data.filter((acc) => acc.is_active));
    } catch (err) {
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.from_account_id || !formData.to_account_id) {
      setError('Please select From and To accounts');
      return;
    }
    if (formData.from_account_id === formData.to_account_id) {
      setError('From and To accounts must be different');
      return;
    }
    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await bankingAPI.createTransfer({
        from_account_id: formData.from_account_id,
        to_account_id: formData.to_account_id,
        amount,
        transaction_date: formData.transaction_date,
        reference: formData.reference.trim() || undefined,
        notes: formData.notes.trim() || undefined,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);

  const toAccountOptions = accounts.filter((acc) => acc.id !== formData.from_account_id);

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title="Transfer between accounts"
      width="w-full max-w-lg"
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-[var(--ds-accent-primary)] border-t-transparent" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div
              className="rounded-[var(--ds-radius-md)] border border-[var(--ds-status-danger)]/30 bg-[var(--ds-status-danger-bg)] px-4 py-3"
              role="alert"
            >
              <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
            </div>
          )}

          <Select
            label="From account"
            name="from_account_id"
            value={formData.from_account_id}
            onChange={handleChange}
            required
            requiredIndicator
            className="h-9"
          >
            <option value="">Select account</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.account_name} ({formatCurrency(acc.current_balance)})
              </option>
            ))}
          </Select>

          <Select
            label="To account"
            name="to_account_id"
            value={formData.to_account_id}
            onChange={handleChange}
            required
            requiredIndicator
            className="h-9"
            disabled={!formData.from_account_id}
          >
            <option value="">Select account</option>
            {toAccountOptions.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.account_name} ({formatCurrency(acc.current_balance)})
              </option>
            ))}
          </Select>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Amount (£)"
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              step="0.01"
              min="0.01"
              required
              placeholder="0.00"
              requiredIndicator
              className="h-9"
            />
            <Input
              label="Date"
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleChange}
              required
              requiredIndicator
              className="h-9"
            />
          </div>

          <Input
            label="Reference (optional)"
            type="text"
            name="reference"
            value={formData.reference}
            onChange={handleChange}
            placeholder="e.g. Ref or transfer ref"
            className="h-9"
          />

          <Textarea
            label="Notes (optional)"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            placeholder="Additional notes..."
            className="min-h-[72px] text-xs"
          />

          <div className="flex justify-end gap-3 border-t border-[var(--ds-border-divider)] pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={saving}>
              {saving ? 'Transferring…' : 'Transfer'}
            </Button>
          </div>
        </form>
      )}
    </SlideOver>
  );
};

export default TransferBetweenAccountsSlideOver;
